import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import SearchIcon from '@mui/icons-material/Search';
import { TextField, MultiSelect, Loading, Switch } from '@shape-ds/components';
import { ChangeEvent, useEffect } from 'react';
import dayjs from 'dayjs';
import { DateFilters, useDatesHelper } from '@shape/lighthouse-components';
import './style.scss';
import { useFilterOptions } from 'pages/Asset/tabs/Events/hooks/useFilterOptions';
import { DateRange } from 'queries/assets/types';

export const TableFilters = <T extends DefaultItem>({
  onChangeFilters,
  onChangeQueryFilters,
  activeQueryFilters,
  categoryFilter,
  periodOpts = ['7', '15', '30', '60', '90'],
  hasDateFilter = true,
  hasVesselFilter,
  hasSwitchFilter,
  hasCategoriesFilter,
  children,
}: TableFiltersProps<T>) => {
  const { t } = useTranslation();
  const { buildDateRange } = useDatesHelper();
  const {
    data: { assetsOptions, categories },
    isLoading,
  } = useFilterOptions(categoryFilter);

  const periodOptions = periodOpts.map((option) => ({
    label:
      option !== 'all'
        ? t('components:date_filter.last_n_days', { days: option })
        : t('components:date_filter.all', { days: option }),
    value: option,
  }));

  const onChangePeriod = (dateRange: DateRange) => {
    const searchParams = new URLSearchParams(location.search);
    searchParams.delete('start');
    searchParams.delete('end');
    navigate({ search: searchParams.toString() }, { replace: true });
    onChangeQueryFilters({
      ...activeQueryFilters,
      start_date: dateRange.startDate,
      end_date: dateRange.endDate,
    });
  };

  const onChangeReferenceDate = (event: ChangeEvent<HTMLInputElement> | boolean) => {
    onChangeQueryFilters({
      ...activeQueryFilters,
      date_reference: typeof event === 'boolean' ? event : event.target.checked,
    });
  };
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const equipment = queryParams.get('equipment');
  const vesselId = queryParams.get('vesselId');
  const period = queryParams.get('period');
  const startDate = queryParams.get('start') ?? null;
  const endDate = queryParams.get('end') ?? null;
  const assetQuery = assetsOptions.find((asset) => asset.value === vesselId);

  useEffect(() => {
    if (equipment) {
      onChangeFilters({
        filterName: 'multipleFieldSearch',
        filterValue: [equipment],
      });
      if (period) {
        onChangePeriod(
          period === 'all' ? { startDate: '2000-01-01', endDate: null } : buildDateRange(period),
        );
      }
    }
    if (startDate && endDate) {
      onChangePeriod({ startDate, endDate });
    }
  }, []);

  if (isLoading)
    return (
      <div className="filters-loading-wrapper">
        <Loading />
      </div>
    );
  return (
    <div className="filters-wrapper">
      <div className="table-filters-search-wrapper">
        <TextField
          icon={<SearchIcon />}
          onChange={(e) =>
            onChangeFilters({
              filterName: 'multipleFieldSearch',
              filterValue: [e.target.value as string],
            })
          }
          placeholder={t('components:event_list.filters.search')}
          data-testid="text-filter"
          fullWidth
          defaultValue={equipment}
          InputProps={{ autoComplete: 'off' }}
        />
      </div>
      {!isLoading && (
        <>
          {hasVesselFilter && (
            <div className="multi-select-wrapper asset-filter">
              <MultiSelect
                data={assetsOptions}
                initialValues={assetQuery?.value ? [assetQuery.value] : ['ALL']}
                allValues={{ label: t('event_page:filters.all_assets'), value: 'ALL' }}
                onChangeCallback={(selectedItems) => {
                  onChangeQueryFilters({
                    ...activeQueryFilters,
                    assets: selectedItems.map((item) => ({ id: item.value, name: item.label })),
                  });
                }}
                data-testid="asset-filter"
                fullWidth
              />
            </div>
          )}
          {hasCategoriesFilter && (
            <div className="multi-select-wrapper category-filter">
              <MultiSelect
                data={categories}
                initialValues={['ALL']}
                allValues={{ label: t('event_page:filters.all_categories'), value: 'ALL' }}
                onChangeCallback={(selectedItems) =>
                  onChangeQueryFilters({
                    ...activeQueryFilters,
                    category: selectedItems.map((item) => item.value),
                  })
                }
                data-testid="category-filter"
              />
            </div>
          )}
        </>
      )}
      {hasSwitchFilter && (
        <div className="switch-holder">
          <div className="switch-title">{t(`components:date_filter.reference_date`)}</div>
          <div className="switch-ui">
            <Switch
              label={t(`components:date_filter.creation`)}
              labelPosition="start"
              checked={activeQueryFilters.date_reference}
              onChange={(value) => onChangeReferenceDate(value)}
              color="default"
            />
            <div
              className="switch-label"
              onClick={() => {
                onChangeReferenceDate(!activeQueryFilters.date_reference);
              }}
            >
              {t(`components:date_filter.closing`)}
            </div>
          </div>
        </div>
      )}
      {hasDateFilter && (
        <div className="select-wrapper">
          <DateFilters
            periodOptions={periodOptions}
            defaultPeriod={period ?? periodOptions[0].value}
            handleCalendarChange={(dateRange) => {
              onChangePeriod(dateRange);
            }}
            handlePeriodChange={(value) =>
              onChangePeriod(
                value === 'all'
                  ? { startDate: '2000-01-01', endDate: null }
                  : buildDateRange(value as string),
              )
            }
            enableCalendar
            enableCustomPeriod
            handleClear={() => {
              onChangePeriod(buildDateRange(period ?? periodOptions[0].value));
            }}
            defaultDateRange={
              startDate ? [dayjs(startDate), endDate ? dayjs(endDate) : null] : undefined
            }
          />
        </div>
      )}
      {children}
    </div>
  );
};
